import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { useForm } from "antd/es/form/Form";
import cn from "classnames";
import { Location } from "history";
import { useHistory } from "react-router-dom";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import { Company, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { LoadingSpinner } from "@mapmycustomers/ui";

import AccessReasonExposer from "@app/component/AccessReasonExposer";
import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import Tabs from "@app/component/preview/components/Tabs";
import consts from "@app/component/preview/styles/_consts.module.scss";
import RecordActionsBar from "@app/component/RecordCard/components/RecordActionsBar";
import ResizableDrawer from "@app/component/ResizableDrawer";
import localSettings from "@app/config/LocalSettings";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { getFunnelStages } from "@app/store/deal";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import eventEmitter from "@app/util/eventEmitter";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import EditDealFormValues from "@app/util/form/EditDealFormValues";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import { dealLayoutModel } from "@app/util/layout/impl";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import FrequencyPanel from "../../frequency/FrequencyPanel";
import Groups from "../components/Groups";
import NoAccess from "../components/NoAccess";
import Title from "../components/Title";
import useTabs from "../util/useTabs";

import Activities from "./Activities";
import styles from "./DealRecordPane.module.scss";
import Details from "./Details";
import Files from "./Files";
import Notes from "./Notes";
import QuickDealEdit from "./QuickDealEdit";
import RelationshipsContainer from "./RelationshipsContainer";
import { DealRecordState } from "./store";
import {
  changeActivitiesFilter,
  createDealNote,
  fetchPreviewData,
  setActiveTab,
  setActivityAutoScrolling,
  setHasChangesFlag,
  updateDealFrequency,
} from "./store/actions";
import DealRecordData from "./store/DealRecordData";
import {
  doesNeedToScrollToActivity,
  getActiveTab,
  getFilesToDeletedIds,
  getRecordData,
  isDealUpdateLoading,
  isLoading,
} from "./store/selectors";

export interface DealRecordPaneProps {
  className?: string;
  dealId: Company["id"];
  isMapMode?: boolean;
  onChange?: (hasChanges: boolean) => void;
  onHide: () => void;
  onShouldShowPrompt: (shouldShow: boolean) => void;
  zIndex?: number;
}

interface Props extends DealRecordPaneProps {
  activeTab: RecordPaneTab | undefined;
  dealUpdateLoading: boolean;
  doesNeedToScrollToActivity?: boolean;
  fetchPreviewData: typeof fetchPreviewData.request;
  filesToDeletedIds: DealRecordState["filesToDeletedIds"];
  funnelStages: Record<Funnel["id"], Stage[]>;
  isReadOnlyMember: boolean;
  loading: boolean;
  onActiveTabChange: typeof setActiveTab;
  onActivitiesFilterChange: typeof changeActivitiesFilter;
  onActivityAutoScrollingChange: typeof setActivityAutoScrolling;
  onHasChangesFlagChange: typeof setHasChangesFlag;
  onNoteAdded: typeof createDealNote.success;
  onUpdateDealFrequency: typeof updateDealFrequency.request;
  recordData: DealRecordData;
}

const DealRecordPane: React.FC<Props> = ({
  activeTab,
  className,
  dealId,
  dealUpdateLoading,
  doesNeedToScrollToActivity,
  fetchPreviewData,
  filesToDeletedIds,
  funnelStages,
  isMapMode,
  isReadOnlyMember,
  loading,
  onActiveTabChange,
  onActivitiesFilterChange,
  onActivityAutoScrollingChange,
  onHasChangesFlagChange,
  onHide,
  onNoteAdded,
  onShouldShowPrompt,
  onUpdateDealFrequency,
  recordData,
  zIndex,
}) => {
  useHiddenChat();

  const history = useHistory();
  const activeTabRef = useRef<HTMLDivElement | null>(null);
  const [detailsForm] = useForm<EditDealFormValues>();
  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();

  const defaultRecordPaneWidth = localSettings.getRecordPaneWidth();
  const [detailsChanged, setDetailsChanged] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);

  const isAnythingChanged = detailsChanged || notesChanged;

  const {
    activities,
    activitiesCompletedByType,
    activitiesTotal,
    associations,
    associationsChanged,
    deal,
    files,
    notes,
  } = recordData;

  useEffect(() => {
    return () => {
      onActivitiesFilterChange?.(ActivityStatusOption.ALL);
    };
  }, [onActivitiesFilterChange]);

  useEffect(() => {
    onShouldShowPrompt(isAnythingChanged);
    onHasChangesFlagChange(isAnythingChanged);
  }, [isAnythingChanged, onShouldShowPrompt, onHasChangesFlagChange]);

  useEffect(() => {
    fetchPreviewData(dealId);
    setDetailsChanged(false);
  }, [dealId, fetchPreviewData, setDetailsChanged]);

  useEffect(() => {
    if (associationsChanged) {
      setDetailsChanged(true);
    }
  }, [associationsChanged]);

  const { defaultTabRedirect, defaultTabsOrder, handleTabsChange, handleTabsOrderChange } = useTabs(
    {
      activeTab,
      entity: deal,
      onActiveTabChange,
    }
  );

  const visibleFiles = useMemo(
    () => files.filter((f) => !filesToDeletedIds.includes(f.id)),
    [files, filesToDeletedIds]
  );

  const stages = useMemo(
    () => (deal ? funnelStages[deal.funnel.id] ?? [] : []),
    [deal, funnelStages]
  );
  const handleOnResize = useCallback((width) => localSettings.setRecordPaneWidth(width), []);

  useLayoutEffect(() => {
    if (doesNeedToScrollToActivity && activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ behavior: "smooth" });
    }
    onActivityAutoScrollingChange(false);
  }, [doesNeedToScrollToActivity, onActivityAutoScrollingChange]);

  const handleActiveTabRefChange = useCallback((newActiveTabRef: HTMLDivElement | null) => {
    activeTabRef.current = newActiveTabRef;
  }, []);

  const handleUpdateFrequency = useCallback(() => {
    if (deal) {
      onUpdateDealFrequency({
        dealId: deal.id,
      });
    }
  }, [deal, onUpdateDealFrequency]);

  const handleShowActivities = useCallback(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth" });
    history.push(RecordPaneTab.ACTIVITIES);
  }, [activeTabRef, history]);

  const schema = useMemo(() => (deal ? dealLayoutModel.getSchemaFor(deal) : undefined), [deal]);

  const filesField = dealFieldModel.getByName(DealFieldName.FILES);
  const notesField = dealFieldModel.getByName(DealFieldName.NOTES);
  const fileSchemaField = useMemo(
    () => schema?.find((schemaField) => schemaField.field === DealFieldName.FILES),
    [schema]
  );

  const handleLeave = useCallback(() => {
    detailsForm.resetFields();
    setDetailsChanged(false);
  }, [detailsForm]);

  const shouldShowPrompt = (location: Location) =>
    isAnythingChanged && location.pathname !== `/${RecordPaneTab.DETAILS}`;

  const handleSave = useCallback(() => {
    eventEmitter.emit("saveDealChanges");
  }, []);

  return (
    <ResizableDrawer
      className={cn(styles.container, className)}
      closable
      defaultWidth={defaultRecordPaneWidth}
      disableResize={isMapMode}
      getContainer={zIndex || isMapMode ? false : getPageContentContainer()}
      mask={false}
      maxWidth={parseInt(consts.maxPaneWidth, 10)}
      onClose={onHide}
      onResize={handleOnResize}
      placement="right"
      title={deal ? <Title entity={deal} entityType={EntityType.DEAL} /> : <br />}
      visible
      zIndex={zIndex}
    >
      {loading ? (
        <LoadingSpinner />
      ) : !deal ? (
        <NoAccess />
      ) : (
        <section className={styles.details}>
          <div className={styles.detailsTopContainer}>
            <div className={styles.columnWrapper}>
              <AccessReasonExposer entity={deal} />
              <RecordActionsBar
                entity={deal}
                onNoteAdded={onNoteAdded}
                people={associations.associatedPerson ? [associations.associatedPerson] : undefined}
              />
              <FrequencyPanel
                entity={deal}
                groups={deal.groups}
                onUpdate={handleUpdateFrequency}
                onViewActivityHistory={handleShowActivities}
              />
              <Groups entity={deal} entityType={EntityType.DEAL} />
            </div>
            <QuickDealEdit
              activeTab={activeTab}
              deal={deal}
              detailsForm={detailsForm}
              formFieldsRef={formFieldsRef}
              handleFinishFail={handleFinishFail}
              onFormChange={setDetailsChanged}
              stages={stages}
            />
          </div>
          <Tabs
            activeTab={activeTab ?? defaultTabRedirect}
            className={styles.tabs}
            defaultTabsOrder={defaultTabsOrder}
            onActiveTabRefChange={handleActiveTabRefChange}
            onChange={handleTabsChange}
            onOrderChange={handleTabsOrderChange}
            tabs={[
              {
                children: (
                  <Details
                    associations={associations}
                    deal={deal}
                    dealUpdateLoading={dealUpdateLoading}
                    detailsForm={detailsForm}
                    formFieldsRef={formFieldsRef}
                    handleFinishFail={handleFinishFail}
                    hasChanges={detailsChanged}
                    onFormChange={setDetailsChanged}
                    onHide={onHide}
                  />
                ),
                tab: RecordPaneTab.DETAILS,
              },
              {
                children: (
                  <Activities
                    activities={activities}
                    activitiesCompletedByType={activitiesCompletedByType}
                    activitiesTotal={activitiesTotal}
                    deal={deal}
                  />
                ),
                tab: RecordPaneTab.ACTIVITIES,
              },
              {
                children: (
                  <RelationshipsContainer
                    deal={deal}
                    parentCompany={associations.associatedCompany}
                    parentPerson={associations.associatedPerson}
                  />
                ),
                tab: RecordPaneTab.RELATIONSHIPS,
              },
              {
                children: (
                  <RelationshipsContainer
                    deal={deal}
                    parentCompany={associations.associatedCompany}
                    parentPerson={associations.associatedPerson}
                  />
                ),
                tab: RecordPaneTab.RELATIONSHIPS,
              },
              {
                children: (
                  <Notes
                    allowedUserIds={deal.userIds}
                    notes={notes}
                    onNotesChange={setNotesChanged}
                    uneditable={!notesField?.isEditable || isReadOnlyMember}
                    unreadable={!notesField?.isReadable}
                  />
                ),
                tab: RecordPaneTab.NOTES,
              },
              {
                children: (
                  <Files
                    accessStatus={deal.accessStatus}
                    files={visibleFiles}
                    required={fileSchemaField?.required}
                    uneditable={!filesField?.isEditable || isReadOnlyMember}
                    unreadable={!filesField?.isReadable}
                  />
                ),
                tab: RecordPaneTab.FILES,
              },
            ]}
          />
          <InterceptNavigationModal
            onLeave={handleLeave}
            onSave={handleSave}
            shouldCloseOnLeave
            shouldShowPrompt={shouldShowPrompt}
          />
        </section>
      )}
    </ResizableDrawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeTab: getActiveTab(state),
  dealUpdateLoading: isDealUpdateLoading(state),
  doesNeedToScrollToActivity: doesNeedToScrollToActivity(state),
  filesToDeletedIds: getFilesToDeletedIds(state),
  funnelStages: getFunnelStages(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isLoading(state),
  recordData: getRecordData(state),
});

const mapDispatchToProps = {
  fetchPreviewData: fetchPreviewData.request,
  onActiveTabChange: setActiveTab,
  onActivitiesFilterChange: changeActivitiesFilter,
  onActivityAutoScrollingChange: setActivityAutoScrolling,
  onHasChangesFlagChange: setHasChangesFlag,
  onNoteAdded: createDealNote.success,
  onUpdateDealFrequency: updateDealFrequency.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealRecordPane);
